import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import type { Company, User } from 'types';

import { isAllowedPage } from 'components/companyAnonReview/companyAnonReviewFormModal';
import GoogleOneTap from 'components/googleOneTapConnect';
import StickyFooterContainer from 'components/stickyFooter/StickyFooterContainer';
import { useCtxHistory, useJobAlertTrigger, useTracking } from 'hooks';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { oneTapGoogleLogin } from 'services/loginService';
import { startPollingForPushSubscriptionChange } from 'services/pushNotificationsService';
import { sessionPopupService } from 'services/sessionPopupService';
import { updateUserInfo } from 'services/userProfileService';
import { trackUserPageViewActivities } from 'services/userService';
import {
  closeLocationInputModal,
  closeLoginModal,
  closePostSignUpPopup,
  dispatchCloseSmsOptinModal,
  dispatchOpenSessionTimerSignupModal,
  dispatchUnhideDLPModal,
  dispatchValidateSessionConditionalSignupModal,
  openPostSignUpPopup,
  openSignUpModal,
} from 'store/modals/actions';
import { __updateuser, updateAnonymousUser } from 'store/user/actions';
import { getCookie, openInCurrentTab, unFreezeBodyScroll, customRedirectOnGoogle } from 'utils';
import { gtmLogSignupEvent, logEventGTM } from 'utils/analyticsGTM';
import { PAGE_CATEGORY, SIGN_UP_TRIGGERS } from 'utils/constant';
import { gtm } from 'utils/gtm';

import surveyPopupClasses from './surveyPopup/surveyPopup.module.css';
import careerMapClasses from 'css/careerMapOverlay.module.css';

const ToastContainer = dynamic(() => import('react-toastify').then((mod) => mod.ToastContainer), {
  ssr: false,
});

/** Modal Components */
const DLP = dynamic(() => import('components/dlp'), { ssr: false });

const ShareJobDLPPopup = dynamic(() => import('components/shareJobDLPPopup'), { ssr: false });

const EasyApplyForm = dynamic(() => import('components/easyApplyForm'), { ssr: false });

const SmsOptinAsDialog = dynamic(() => import('layouts/profilePage/smsOptinAsDialog'), {
  ssr: false,
});

const AdviceArticlePopup = dynamic(() => import('components/adviceArticle'));

const SessionTimerSignupPopup = dynamic(() => import('components/sessionTimerSignupPopup'));

const SessionConditionalSignupPopup = dynamic(
  () => import('components/sessionConditionalSignupPopup')
);

const EmbedWidgetPopup = dynamic(() => import('components/embedWidgetPopup'));

const RecommendedJobBubble = dynamic(() => import('components/popups/recommendedJobBubble'), {
  ssr: false,
});

const LoginPopup = dynamic(() => import('components/LoginPopup'), {
  ssr: false,
});

const SurveyPopup = dynamic(() => import('components/surveyPopup/SurveyPopup'), { ssr: false });

const LocationInputFieldPopup = dynamic(
  () => import('components/jobs/card/LocationInputFieldPopup'),
  { ssr: false }
);

const PostSignUpFormPopup = dynamic(() => import('components/postSignUpFormPopup'), { ssr: false });

const SignupRecaptchaModal = dynamic(() => import('components/recaptcha/signupRecaptchaModal'), {
  ssr: false,
});

const PostApplyUXModal = dynamic(() => import('components/dlp/detail/postApplyUX'), { ssr: false });

const GatedSignupPopup = dynamic(() => import('components/gatedSignupPopup'), {
  ssr: false,
});

const NativeNotificationPrompt = dynamic(() => import('components/nativeNotificationPrompt'), {
  ssr: false,
});

const RBTemplatesModal = dynamic(() => import('components/resumeBuilder/popup/templates'), {
  ssr: false,
});

const RBDeletePopup = dynamic(() => import('components/resumeBuilder/popup/delete'), {
  ssr: false,
});

const RBSubscriptionModal = dynamic(() => import('components/resumeBuilder/popup/subscription'), {
  ssr: false,
});

const ResetPasswordModal = dynamic(() => import('components/resumeBuilder/popup/resetPassword'), {
  ssr: false,
});

const ResumePreviewModal = dynamic(() => import('components/resumeBuilder/preview/modal'), {
  ssr: false,
});

const JobAlertPopup = dynamic(() => import('components/jobAlertPopup'), { ssr: false });

const RBFormatDrawer = dynamic(() => import('components/resumeBuilder/popup/rbFormatDrawer'), {
  ssr: false,
});

const ExitIntentPopup = dynamic(() => import('components/exitIntentPopup'), { ssr: false });

const TrackingPixels = dynamic(
  () => import('components/trackingPixels').then((mod) => mod.TrackingPixels),
  { ssr: false }
);

const CompanyAnonReviewFormModal = dynamic(
  () => import('components/companyAnonReview/companyAnonReviewFormModal'),
  {
    ssr: false,
  }
);

interface IProps {
  user?: User;
  companyInfo?: Company;
  averageClickProb?: number;
  showEmployerPopout?: boolean;
  isCareerEducationPage?: boolean;
  isCompanyLocationsPage?: boolean;
  isCareerDescriptionPage?: boolean;
  isCareerHowToHirePage?: boolean;
  hidePersonalizedJobs?: boolean;
  hasApprovedReviews?: boolean;
}

export const ModalContainerPropFields = [
  'user',
  'companyInfo',
  'averageClickProb',
  'showEmployerPopout',
  'isCareerEducationPage',
  'isCompanyLocationsPage',
  'isCareerDescriptionPage',
  'isCareerHowToHirePage',
  'hidePersonalizedJobs',
  'hasApprovedReviews',
];

const ModalContainer = (props: IProps) => {
  const {
    averageClickProb,
    showEmployerPopout,
    isCareerDescriptionPage,
    isCareerHowToHirePage,
    hidePersonalizedJobs,
  } = props;

  // Note: The HTTP_ACTIVITY will be logged in the page itself so
  // removing the additional logging here. If you want to remove the
  // HTTP_ACTIVITY being logged by default then add the page category in
  // the list below
  const logHTTPActivity = ![
    'Company Profile Settings',
    // Add more page category here if you want to remove HTTP_ACTIVITY being logged from here
  ].includes(window.pageCategory);

  const {
    modals,
    criteria,
    userGroup,
    isUserLoggedIn,
    previewModalInfo,
    googleOneTapClientId,
    enablePostSignUpSurvey,
    enableSignUpPopupForUserSession,
  } = useAppSelector((state) => ({
    modals: state.modals,
    criteria: state.locals.criteria,
    userGroup: state.locals.userGroup,
    isUserLoggedIn: state.user.isLoggedIn,
    previewModalInfo: state.resumeBuilder.previewModalInfo,
    googleOneTapClientId: state.locals.googleOneTapClientId,
    enablePostSignUpSurvey: state.locals.enablePostSignUpSurvey,
    enableSignUpPopupForUserSession: state.locals.enableSignUpPopupForUserSession,
  }));

  const isLocationInputPopupOpen = modals.locationInputPopup.open;

  const isPostSignUpPopupOpen = modals.postSignUpSurveyPopup.open;

  const locationInputPurpose = modals.locationInputPopup.purpose;

  const dispatch = useAppDispatch();

  const history = useCtxHistory();

  const tracking = useTracking();

  const jobAlertProps = useJobAlertTrigger({ criteria, companyInfo: props.companyInfo });

  useEffect(() => {
    // Log HTTP_ACTIVITY
    const httpActivityPayload = {
      averageClickProb,
      pageJobTitle: criteria?.title || undefined,
      pageCity: criteria?.city || undefined,
      pageState: criteria?.state || undefined,
      pageCategory: window.pageCategory,
    };

    let zippiaUser = props.user;

    const openSessionTimerModal = () => {
      dispatch(dispatchOpenSessionTimerSignupModal({ method: SIGN_UP_TRIGGERS.TIMER }));
      gtm.logSignUpTriggerDisplayed(SIGN_UP_TRIGGERS.TIMER);
    };

    function setupSessionPopups() {
      const isOneTapSurvey =
        modals.signUp.oneTapSurvey && modals.signUp.survey === '039'
          ? surveyPopupClasses['oneTapSurvey']
          : '';
      if (!hidePersonalizedJobs && !isOneTapSurvey) {
        sessionPopupService.handleSessionTimerPopup(
          zippiaUser,
          enableSignUpPopupForUserSession,
          modals.sessionTimerSignup,
          openSessionTimerModal
        );
      }
      dispatch(dispatchValidateSessionConditionalSignupModal());
    }
    if (logHTTPActivity) {
      try {
        tracking.logActivity(httpActivityPayload).then(({ data }) => {
          const { user } = data;
          dispatch(updateAnonymousUser(user));
          zippiaUser = user;
          if (!window.userId) {
            window.userId = user.userId;
          }
          window.isLoggedIn = user.isLoggedIn || false;
          logEventGTM();
        });
      } catch (err) {}
    }
    setupSessionPopups();
    if (zippiaUser?.userId) startPollingForPushSubscriptionChange(zippiaUser);
    trackUserPageViewActivities(criteria);
  }, []);

  const onSignUpPopup = () => {
    const surveyStepSituation = getCookie('_zippia-surveyStepSituation');
    const survey = surveyStepSituation === 'groupB' ? '040' : '036';
    const query = {
      survey_step: 'step1',
      survey,
      utm_campaign: 'nav_sign_up',
    };
    dispatch(
      openSignUpModal({
        ...query,
        popupType: 'header-sign-up',
      })
    );
    history.pushQuery(query);
  };

  const handleCredentialResponse = async ({ credential }) => {
    try {
      const response = await oneTapGoogleLogin(credential);
      const { user } = response.data;
      const { isNewUser } = user;

      // Add location to user profile if this is a new user.
      if (isNewUser && (criteria.geoCity || criteria.geoState)) {
        const { geoCity = '', geoState = '', geoStateCode = '' } = criteria;
        const location = [
          {
            city: geoCity,
            state: geoCity ? geoStateCode : geoState,
          },
        ];
        await updateUserInfo({ location }, 'location');
      }

      dispatch(__updateuser(user));

      gtm.logSignUpTriggerSucceed(SIGN_UP_TRIGGERS.ONE_TAP);

      if (user && isNewUser && enablePostSignUpSurvey) {
        gtmLogSignupEvent('Google Identity');
        dispatch(closeLoginModal());
        dispatch(
          openPostSignUpPopup({
            survey: '043',
            survey_step: 'step1',
            src: 'continueWithGoogle',
          })
        );
        return;
      }
      if (!customRedirectOnGoogle(window.location.href)) {
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const renderOneTapGoogleLogin = () => {
    return (
      <GoogleOneTap
        showDisplayOr={false}
        googleClientId={googleOneTapClientId}
        onGoogleOneTap={handleCredentialResponse}
      />
    );
  };

  const handleLocationInputPopupClose = () => {
    dispatch(closeLocationInputModal());
  };

  const handlePostSignUpPopupClose = () => {
    dispatch(closePostSignUpPopup());
    window.location.reload();
  };

  const getRecommendedBubbleSrc = () => {
    if (isCareerDescriptionPage) {
      return '#src=job-description-callout-emp4014-b30';
    }
    if (isCareerHowToHirePage) {
      return '#src=hth-callout-emp4014-b50';
    }

    return '';
  };

  const isSignProcessClosed =
    !modals.signUp?.open && !modals.gatedSignup?.open && !modals.login?.open;

  const handleSmsOptinClose = () => {
    dispatch(dispatchCloseSmsOptinModal());

    if (modals.smsOptin?.redirectTo) {
      openInCurrentTab(modals.smsOptin.redirectTo);
      return;
    }

    if (modals.smsOptin?.unhideDlpAfterClose) {
      dispatch(dispatchUnhideDLPModal());
    } else {
      unFreezeBodyScroll();
    }
  };

  /**
   * not showing the UI part of popup if there is currently a popup on the screen
   * after closing another popup we show conditional popup or timer popup
   * 
   * it's temp solution since we plan to update logic and add something like queue for popups
   */
  const isAnyPopupOpenExceptSessionConditional = Object.entries(modals)
    .filter(([key]) => !['sessionConditionalSignup', 'oneTapInit'].includes(key))
    .some(([, modal]) => modal?.open);
  const isAnyPopupOpenExceptSessionTimer = Object.entries(modals)
    .filter(([key]) => !['sessionTimerSignup', 'oneTapInit'].includes(key))
    .some(([, modal]) => modal?.open);

  return (
    <>
      <LoginPopup openSignUpPopup={onSignUpPopup} />
      {/* signup modal popup */}
      {(modals.signUp.open || isLocationInputPopupOpen) &&
        !modals.signUp.surveyFullPage &&
        !modals.signUp.oneTapSignUp && (
          <div
            className={`${careerMapClasses['career-map-overlay']} ${
              modals.postApplyUX.open ? 'post-apply-ux-overlay' : ''
            }`}
          />
        )}
      <SurveyPopup />

      {!isUserLoggedIn && renderOneTapGoogleLogin()}

      {/* Location Input Popup will be displayed when user is not signed in
                and try to save/dismiss the job he/she is interested in */}
      <LocationInputFieldPopup
        purpose={locationInputPurpose}
        open={isLocationInputPopupOpen}
        onCloseModel={handleLocationInputPopupClose}
      />

      <PostSignUpFormPopup open={isPostSignUpPopupOpen} onCloseModel={handlePostSignUpPopupClose} />

      {/* DLP as popup, open only if dlp as isPopUp */}
      {modals.dlp.open && !(modals.login.open || modals.signUp.open) && modals.dlp.isPopUp && (
        <DLP />
      )}

      {modals.postApplyUX.open && <PostApplyUXModal />}

      {modals.share.open && modals.share.type === 'job' && <ShareJobDLPPopup />}

      {modals.easyApply.open && <EasyApplyForm />}

      {modals.smsOptin.open && (
        <SmsOptinAsDialog
          user={props.user}
          prefilledPhone={modals.smsOptin?.phone || ''}
          isOpen={modals.smsOptin.open}
          onClose={handleSmsOptinClose}
          onOptoutSuccess={handleSmsOptinClose}
        />
      )}

      {modals.adviceArticlePopup.articlePopupOpen && <AdviceArticlePopup />}

      {modals.sessionTimerSignup.open && isSignProcessClosed && !isAnyPopupOpenExceptSessionTimer && <SessionTimerSignupPopup />}

      <NativeNotificationPrompt />

      {modals.sessionConditionalSignup.open && !isAnyPopupOpenExceptSessionConditional && <SessionConditionalSignupPopup />}

      {modals.embedWidget.open && <EmbedWidgetPopup />}

      {modals.signupRecaptcha.open && <SignupRecaptchaModal />}

      {modals.resumeBuilderModals.templates && <RBTemplatesModal />}

      {modals.resumeBuilderModals.delete && <RBDeletePopup />}

      {modals.resumeBuilderModals.subscription && <RBSubscriptionModal />}

      {modals.resumeBuilderModals.resetPassword && <ResetPasswordModal />}

      {!!previewModalInfo.open && <ResumePreviewModal />}

      {modals.jobAlert.open && <JobAlertPopup {...jobAlertProps} />}

      {window?.location?.pathname !== '/resume-builder/' && window?.userId && <GatedSignupPopup />}

      <ExitIntentPopup
        location={[
          {
            city: criteria?.geoCity || '',
            state: (criteria?.geoCity ? criteria?.geoStateCode : criteria?.geoState) || '',
          },
        ]}
      />

      {modals.rbFormatDrawer.open && <RBFormatDrawer />}

      {!!showEmployerPopout && (
        <RecommendedJobBubble
          isEmployerPopout={showEmployerPopout}
          criteria={criteria}
          src={getRecommendedBubbleSrc()}
        />
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        closeButton={false}
      />

      <TrackingPixels />

      <StickyFooterContainer
        // Show the "Sticky Footer Container" only on Career Salary Page
        pageCategories={[PAGE_CATEGORY.careerSalaryPage]}
        globalTurnOn={userGroup?.['stickyFooterResumes'] === 'groupB'}
      />

      {window && isAllowedPage(window.pageCategory) && (
        <CompanyAnonReviewFormModal
          companyName={props.companyInfo?.companyNameCPS}
          companyID={props.companyInfo?.companyID}
          hasApprovedReviews={props.hasApprovedReviews}
        />
      )}
    </>
  );
};

export default ModalContainer;
